import { useState, Fragment, useCallback } from "react";
import { Box, Button, Typography, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { IoChevronBackCircleOutline } from "react-icons/io5";
import { FiGitCommit, FiCircle, FiUserCheck, FiHexagon, FiPhoneCall, FiHome } from "react-icons/fi";
import { CV_INFORMATION_API, FILE_MANAGEMENT_API } from "../api";
import { ReactComponent as Logo } from "../assets/icons/Logo.svg";
import { PALETTE } from "../Theme/Palette";
import { useEffect } from "react";

const Image = ({ src }) => {
  const [image, setImage] = useState();
  const { mutateAsync: asyncDownloadImage } = useMutation((url) =>
    FILE_MANAGEMENT_API.Download(url)
  );

  const handleLoadImage = useCallback(
    async (srcLink) => {
      const response = await asyncDownloadImage(srcLink);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      setImage(URL.createObjectURL(blob));
    },
    [asyncDownloadImage]
  );

  useEffect(() => {
    handleLoadImage(src);
  }, [handleLoadImage, src]);

  return (
    <Box
      width="100%"
      position="relative"
      sx={{
        boxSizing: "border-box",
        padding: 0,
        marginY: "1rem",
        transition: "padding 0.3s ",
        backgroundColor: "transparent",
        borderRadius: "1rem",
      }}
    >
      <img
        style={{
          width: "100%",
          borderRadius: 0,
        }}
        src={image}
        alt=""
      />
    </Box>
  );
};

const CenterResume = () => {
  const navigate = useNavigate();
  const { branchId } = useParams();

  const USER_INFO_SHAPES = [
    { icon: FiUserCheck, title: "مدیریت مرکز", branchInfoKey: "managerName" },
    { icon: FiGitCommit, title: "استان", branchInfoKey: "provinceName" },
    { icon: FiCircle, title: "شهر", branchInfoKey: "cityName" },
    { icon: FiHexagon, title: "منطقه", branchInfoKey: "region" },
    { icon: FiPhoneCall, title: "تلفن ثابت", branchInfoKey: "landPhoneNumber" },
    { icon: FiHome, title: "آدرس", branchInfoKey: "address" },
  ];

  const { data } = useQuery(["GetBranchCVInformation", branchId], () =>
    CV_INFORMATION_API.GetBranchCVInformation(branchId)
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexDirection="row-reverse"
        width="100%"
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          <Button
            onClick={() => navigate(-1)}
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
          >
            <Typography variant="h2" fontSize="1.3rem" fontWeight="bold" marginRight="0.7rem">
              روزمه مرکز
            </Typography>
            <IoChevronBackCircleOutline
              color={PALETTE.fourth}
              fontSize="1.2rem"
              style={{ transform: "rotate(180deg)" }}
            />
          </Button>
        </Box>
      </Box>
      <Box
        width="100%"
        maxWidth="820px"
        marginTop="4rem"
        sx={{ direction: "rtl", boxSizing: "border-box", paddingX: { xs: 2, sm: 5 } }}
      >
        <Box
          display="flex"
          justifyContent={{ xs: "space-between", sm: "flex-start" }}
          alignItems="center"
          marginBottom="3rem"
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            marginLeft={{ xs: "0", sm: "1.5rem" }}
            sx={{
              padding: "1rem",
              borderRadius: "50%",
              border: `2px solid ${PALETTE.light_pink}80`,
              aspectRatio: "1/1",
            }}
          >
            <Logo />
          </Box>
          <Box>
            <Typography variant="h1" fontSize="1.8rem" fontWeight="bold">
              مرکز {data?.data.data.name}
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent={{ xs: "flex-start", sm: "center" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              marginRight="0.5rem"
            >
              <Typography variant="subtitle1" component="pre" paddingY="0.5rem">
                مدیر مرکز: {data?.data.data.managerName}
                {" - "}
              </Typography>
              <Typography variant="subtitle1">{data?.data.data.managerJobTitle}</Typography>
            </Box>
          </Box>
        </Box>
        <Grid container>
          {USER_INFO_SHAPES.map((item, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
            >
              <Grid
                display="flex"
                justifyContent="flex-start"
                alignItems="flex-end"
                item
                xs={1}
                paddingY="0.6rem"
                pl={{ xs: "0.4rem", sm: 0 }}
              >
                <item.icon color={PALETTE.third} style={{ opacity: 0.4 }} fontSize="1.2rem" />
              </Grid>
              <Grid item xs={4} sm={3} paddingY="0.6rem">
                <Typography variant="subtitle1">{item.title}</Typography>
              </Grid>
              <Grid item xs={7} sm={8} paddingY="0.6rem">
                <Typography variant="subtitle1">{data?.data.data[item.branchInfoKey]}</Typography>
              </Grid>
            </Box>
          ))}
        </Grid>
        {data?.data.data.contentBody?.map((item, index) => (
          <Fragment key={index}>
            {item.isLink ? (
              <Image index={index} src={item.text} />
            ) : (
              <Typography variant="subtitle1" my="1rem">
                {item.text}
              </Typography>
            )}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default CenterResume;
